import React, { useMemo } from "react";
import ChartCanvas from "./ChartCanvas";
import ChartFrame from "./ChartFrame";
import { valueLabelsPlugin } from "./chartPlugins";
import {
  chartColors,
  formatCompactNumber,
  mergeOptions,
  resolveTone,
  verticalGradient,
} from "./chartTheme";

/**
 * Configurable bar / column chart.
 *
 * Data shape — one row per category, one key per series:
 *   data   = [{ label: "Q1", newBusiness: 12, renewal: 4 }, …]
 *   series = [{ key: "newBusiness", label: "New Business", tone: "green" },
 *             { key: "renewal",     label: "Renewal",      tone: "blue"  }]
 *
 * `tone` is a gradient name from chartTheme (green/blue/violet/…), a palette
 * index, or an explicit `{ dark, light }` pair. A single series with no tone
 * cycles a different colour per bar.
 *
 * Anything not covered by a prop can be reached with `options`, which is
 * deep-merged over the generated Chart.js options.
 */
export default function BarChart({
  /* frame */
  title,
  subtitle,
  icon,
  note,
  actions,
  hint,
  onBack,
  loading = false,
  error = null,
  emptyText = "No data available.",
  height = 360,
  /* data */
  data = [],
  series = [],
  labelKey = "label",
  /* axes + formatting */
  stacked = false,
  maxY,
  stepSize,
  valueSuffix = "",
  yAxisLabel = "",
  compactYAxis = false,
  /* behaviour */
  showLegend,
  showValueLabels,
  tooltipAfterBody,
  onBarClick,
  type = "bar",
  options: optionsOverride,
  ariaLabel,
}) {
  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const seriesDefs = useMemo(
    () => (series.length ? series : [{ key: "value", label: title || "Value" }]),
    [series, title],
  );

  /* One series with no explicit tone → colour every bar differently. */
  const cycleTones = seriesDefs.length === 1 && seriesDefs[0].tone == null;
  const withLegend = showLegend ?? seriesDefs.length > 1;
  const withValueLabels = showValueLabels ?? stacked;

  const chartData = useMemo(
    () => ({
      labels: rows.map((row) => row[labelKey]),
      datasets: seriesDefs.map((serie, index) => {
        const toneOf = (ctx) => (cycleTones ? ctx.dataIndex : serie.tone ?? index);
        const isTopOfStack = index === seriesDefs.length - 1;
        return {
          label: serie.label ?? serie.key,
          data: rows.map((row) => row[serie.key] ?? 0),
          backgroundColor: (ctx) => verticalGradient(ctx.chart, toneOf(ctx)),
          hoverBackgroundColor: (ctx) => verticalGradient(ctx.chart, toneOf(ctx)),
          borderColor: cycleTones
            ? (ctx) => resolveTone(ctx.dataIndex).dark
            : undefined,
          borderWidth: cycleTones ? 1 : 0,
          borderRadius: stacked
            ? isTopOfStack
              ? { topLeft: 6, topRight: 6, bottomLeft: 0, bottomRight: 0 }
              : 0
            : 8,
          borderSkipped: false,
          barPercentage: 0.55,
          categoryPercentage: 0.7,
        };
      }),
    }),
    [rows, seriesDefs, labelKey, cycleTones, stacked],
  );

  const chartOptions = useMemo(() => {
    const color = chartColors();
    const tickCallback = compactYAxis
      ? (value) => formatCompactNumber(value)
      : (value) => `${value}${valueSuffix}`;

    const base = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      onClick: (_evt, elements) => {
        if (!onBarClick || !elements?.length) return;
        const index = elements[0].index;
        onBarClick(rows[index], index);
      },
      onHover: (evt, elements) => {
        const canvas = evt?.native?.target;
        if (canvas) {
          canvas.style.cursor = onBarClick && elements.length ? "pointer" : "default";
        }
      },
      scales: {
        x: {
          stacked,
          grid: { display: false },
          border: { display: false },
          ticks: { color: color.label, font: { size: 11, weight: "500" } },
        },
        y: {
          stacked,
          beginAtZero: true,
          max: maxY,
          grid: { color: color.grid },
          border: { display: false },
          ticks: { stepSize, color: color.muted, callback: tickCallback },
          title: {
            display: Boolean(yAxisLabel),
            text: yAxisLabel,
            color: color.muted,
            font: { size: 13, weight: "600" },
          },
        },
      },
      plugins: {
        legend: {
          display: withLegend,
          position: "bottom",
          align: "center",
          labels: {
            usePointStyle: true,
            pointStyle: "rect",
            boxWidth: 14,
            boxHeight: 10,
            padding: 16,
            font: { size: 12, weight: "500" },
            color: color.muted,
            filter: (item) => item.text?.trim() !== "",
          },
        },
        tooltip: {
          mode: "index",
          intersect: false,
          backgroundColor: color.tooltipBg,
          padding: 12,
          cornerRadius: 8,
          titleFont: { weight: "bold" },
          callbacks: {
            label: (ctx) =>
              ctx.dataset.label?.trim()
                ? ` ${ctx.dataset.label}: ${ctx.parsed.y}${valueSuffix}`
                : null,
            afterBody: (items) => {
              if (typeof tooltipAfterBody !== "function" || !items?.length) return [];
              const extra = tooltipAfterBody(items[0].dataIndex);
              if (extra == null) return [];
              return Array.isArray(extra) ? extra : [extra];
            },
          },
        },
        valueLabels: withValueLabels ? { suffix: valueSuffix } : false,
      },
    };

    return mergeOptions(base, optionsOverride);
  }, [
    rows,
    stacked,
    maxY,
    stepSize,
    valueSuffix,
    yAxisLabel,
    compactYAxis,
    withLegend,
    withValueLabels,
    tooltipAfterBody,
    onBarClick,
    optionsOverride,
  ]);

  const plugins = useMemo(
    () => (withValueLabels ? [valueLabelsPlugin] : []),
    [withValueLabels],
  );

  return (
    <ChartFrame
      title={title}
      subtitle={subtitle}
      icon={icon}
      note={note}
      actions={actions}
      hint={hint}
      onBack={onBack}
      loading={loading}
      error={error}
      empty={rows.length === 0}
      emptyText={emptyText}
      height={height}
    >
      <ChartCanvas
        type={type}
        data={chartData}
        options={chartOptions}
        plugins={plugins}
        ariaLabel={ariaLabel || title}
      />
    </ChartFrame>
  );
}
